import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HashtagType } from '@anytag/graphql';
import { Form, NoticeV2 } from '@anytag/shared-atoms';
import { formatHashTags } from '@anytag/shared-utils';
import { HashtagCloud } from '../hashtag-cloud';
import { HashTagForm } from '../hash-tag-form';
import { RHFFieldWrapper } from './shared';
import { DefaultFieldProps } from './types';
import { useFormatRHFFieldProps } from './hooks';

export interface HashtagSuggestionFieldProps<T extends string> extends DefaultFieldProps<T, string[]> {
  maxHashtags?: number;
  notice?: ReactNode;
  hasHashTagForm?: boolean;
  getHashtagsCloud?: () => void;
  onTagClick?: (tag: string) => void;
  hashtagsCloud?: readonly HashtagType[];
}

export const HashtagSuggestionField = <T extends string>({
  className,
  notice,
  maxHashtags = 9, // TODO: this number 9 is hardcoded, but biz logic is not clear
  ...props
}: HashtagSuggestionFieldProps<T>) => {
  const { t } = useTranslation();
  // help to prevent onHashTagFormBlur on being called when hashtag is clicked
  const [hoverHashtagCloud, setHoverHashtagCloud] = useState(false);
  const [hashtagIndex, setHashtagIndex] = useState<number | null>(null);
  const {
    fieldValue,
    fieldRegister,
    setValueOptions,
    fieldWrapperProps,
    formContext: { trigger, setValue },
    fieldProps: {
      name,
      onChange,
      onTagClick,
      getHashtagsCloud,
      hashtagsCloud = [],
      hasHashTagForm = true,
      ...restFieldProps
    }
  } = useFormatRHFFieldProps(props);

  const isReachedHashtagsLimit = fieldValue?.length >= maxHashtags;

  const handleChangeHashtags = (newHashtags: string[]) => {
    if (onChange) {
      onChange(name, newHashtags);
    } else {
      setValue<string>(name, newHashtags, setValueOptions);
      trigger(name);
    }
  };

  const onHashTagFormBlur = () => {
    if (!hoverHashtagCloud) {
      handleChangeHashtags(formatHashTags(fieldValue));
    }
    getHashtagsCloud?.();
  };

  const handleTagClick = (value: string) => {
    let newIndex = hashtagIndex || fieldValue.length;
    const emptyTagIndex = fieldValue.indexOf('');
    // no empty inputs & no focused inputs
    if (emptyTagIndex !== -1 && !hashtagIndex) {
      newIndex = emptyTagIndex;
    }
    // reach hashtag limit & no empty hashtag inputs
    if (isReachedHashtagsLimit && emptyTagIndex === -1) {
      return;
    }

    fieldValue[newIndex] = value; // set new hashtag value
    setHashtagIndex(null); // clear focused input
    setValue<string>(name, formatHashTags(fieldValue), setValueOptions);
    trigger(name);
    getHashtagsCloud?.();
  };

  const toggleMouseEnter = () => {
    setHoverHashtagCloud(true);
  };

  const toggleMouseOut = () => {
    setHoverHashtagCloud(false);
  };

  return (
    <Form.Fields itemsPerRow={hasHashTagForm ? 2 : 1} className={className}>
      <Form.Focus ref={fieldRegister.ref} />
      {hasHashTagForm ? (
        <RHFFieldWrapper
          {...fieldWrapperProps}
          title={fieldWrapperProps.title ?? t('TextForm.Hash Tags')}
          help={fieldWrapperProps.help ?? t('Tooltip.At least three hashtags')}
          dynamicError
        >
          <HashTagForm
            {...restFieldProps}
            maxHashtags={maxHashtags}
            onBlur={onHashTagFormBlur}
            onChange={(value) => {
              setValue<string>(name, formatHashTags(value), setValueOptions);
              trigger(name);
            }}
          />
          {isReachedHashtagsLimit && notice ? <NoticeV2 description={notice} className="mt-3" /> : <div />}
        </RHFFieldWrapper>
      ) : null}

      {hashtagsCloud?.length > 0 ? (
        <RHFFieldWrapper
          onMouseLeave={toggleMouseOut}
          onMouseEnter={toggleMouseEnter}
          title={t('Recommended Hash Tags')}
          help={t('Tooltip.HastagRecommendationHelp')}
        >
          <HashtagCloud onSelect={onTagClick || handleTagClick} data={hashtagsCloud} />
        </RHFFieldWrapper>
      ) : null}
    </Form.Fields>
  );
};
